/**
 * useNotifications.ts — Centre de notifications (#12+13 P1.1).
 * TanStack Query v5.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchNotifications,
  fetchUnreadCount,
  setNotificationRead,
} from '../lib/api'

export const NOTIFICATIONS_QK   = ['notifications', 'list'] as const
export const UNREAD_COUNT_QK    = ['notifications', 'unread-count'] as const

export function useNotifications(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey:  [...NOTIFICATIONS_QK, params ?? {}],
    queryFn:   () => fetchNotifications(params),
    staleTime: 30_000,
  })
}

/** Le compteur de la future cloche (P1.6) — il vit déjà, branché sur du réel. */
export function useUnreadCount() {
  return useQuery({
    queryKey:  UNREAD_COUNT_QK,
    queryFn:   fetchUnreadCount,
    staleTime: 30_000,
  })
}

export function useSetNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => setNotificationRead(id, read),
    /**
     * ⚠⚠ **LA BASCULE EST OPTIMISTE — ET C'EST UN CORRECTIF, PAS UN CONFORT.**
     *    *Mesuré sur l'écran de Hkm (10/09) : « le passage de read a unread est
     *    super lent, temps d'attente 3s ». La cause n'était pas le réseau — la
     *    route répond vite — mais l'ordre des choses : on **attendait la
     *    réponse**, puis on **réinvalidait la liste entière** (181
     *    notifications) avant de repeindre. **Le clic payait un aller-retour ET
     *    un rechargement complet.***
     *
     * ⚠ *Le cache est corrigé **avant** l'appel ; l'invalidation reste, mais en
     *   `onSettled` — elle rattrape en arrière-plan sans bloquer l'œil. En cas
     *   d'échec, `onError` **remet exactement ce qui était là** : un état
     *   optimiste qu'on ne saurait pas défaire mentirait.*
     */
    onMutate: async ({ id, read }) => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_QK })
      const avant = qc.getQueriesData({ queryKey: NOTIFICATIONS_QK })
      for (const [cle] of avant) {
        qc.setQueryData(cle, (vieux: unknown) => majLecture(vieux, id, read))
      }
      return { avant }
    },
    onError: (_e, _v, ctx) => {
      for (const [cle, valeur] of ctx?.avant ?? []) qc.setQueryData(cle, valeur)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QK })
      void qc.invalidateQueries({ queryKey: UNREAD_COUNT_QK })
    },
  })
}

/**
 * ⚠ *Le patch d'une réponse en cache — `{ data: { data: [...] } }`. Une forme
 *   inattendue est rendue **telle quelle** : mieux vaut ne rien peindre que
 *   corrompre un cache qu'on n'a pas compris.*
 */
function majLecture(vieux: unknown, id: string, read: boolean): unknown {
  const r = vieux as { data?: { data?: Array<{ id: string; read: boolean }> } } | undefined
  if (!Array.isArray(r?.data?.data)) return vieux
  return {
    ...r,
    data: {
      ...r.data,
      data: r.data.data.map((n) => (n.id === id ? { ...n, read } : n)),
    },
  }
}
